"use client";

import { SURVEYS_QUERY } from "@/graphql/queries/surveyQueries";
import { useQuery } from "@apollo/client";
import {
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ClipboardDocumentListIcon,
    LockClosedIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { Link } from "react-router-dom";

interface Survey {
    id: string;
    title: string;
    description: string;
    vpReward: number;
    status: string;
    endDate: string | null;
    myResponse: { id: string } | null;
}

export default function SurveysListPage() {
    const { data, loading, error } = useQuery(SURVEYS_QUERY);
    const [showCompleted, setShowCompleted] = useState(false);
    const [showClosed, setShowClosed] = useState(false);

    const surveys: Survey[] = data?.surveys ?? [];

    const available = surveys.filter(
        (s) => s.status === "active" && !s.myResponse,
    );
    const completed = surveys.filter(
        (s) => s.status === "active" && s.myResponse,
    );
    const closed = surveys.filter((s) => s.status === "closed");

    if (loading) {
        return (
            <div className="fixed inset-0 z-10 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
                <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 z-10 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
                <div className="rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                    Error loading surveys: {error.message}
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-10 overflow-y-auto bg-slate-950/80 pt-16 backdrop-blur-sm">
            <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    Surveys
                </h1>

                {surveys.length === 0 ? (
                    <div className="rounded-2xl border border-slate-300 bg-white p-8 text-center shadow-lg dark:border-slate-700 dark:bg-slate-900">
                        <p className="text-slate-600 dark:text-slate-400">
                            No surveys available right now.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Available surveys */}
                        {available.length > 0 && (
                            <div className="space-y-3">
                                <h2 className="text-sm font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
                                    Available ({available.length})
                                </h2>
                                {available.map((survey) => (
                                    <Link
                                        to={`/dashboard/surveys/${survey.id}`}
                                        key={survey.id}
                                        className="flex items-center justify-between rounded-2xl border border-slate-300 bg-white p-4 shadow-lg transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
                                    >
                                        <div className="flex items-center gap-3">
                                            <ClipboardDocumentListIcon className="text-brand h-6 w-6 shrink-0" />
                                            <div>
                                                <p className="font-medium text-slate-900 dark:text-slate-100">
                                                    {survey.title}
                                                </p>
                                                {survey.description && (
                                                    <p className="mt-0.5 line-clamp-1 text-sm text-slate-600 dark:text-slate-400">
                                                        {survey.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            {survey.vpReward > 0 && (
                                                <span className="text-brand text-sm font-semibold">
                                                    +{survey.vpReward} VP
                                                </span>
                                            )}
                                            {survey.endDate && (
                                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                                    Ends{" "}
                                                    {new Date(
                                                        survey.endDate,
                                                    ).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}

                        {available.length === 0 && (
                            <div className="rounded-2xl border border-slate-300 bg-white p-6 text-center shadow-lg dark:border-slate-700 dark:bg-slate-900">
                                <p className="text-slate-500 dark:text-slate-400">
                                    No new surveys available.
                                </p>
                            </div>
                        )}

                        {/* Completed surveys (accordion) */}
                        {completed.length > 0 && (
                            <div>
                                <button
                                    onClick={() => setShowCompleted((v) => !v)}
                                    className="flex w-full items-center justify-between rounded-lg py-2 text-left"
                                >
                                    <h2 className="text-sm font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
                                        Completed ({completed.length})
                                    </h2>
                                    {showCompleted ? (
                                        <ChevronUpIcon className="h-4 w-4 text-slate-400" />
                                    ) : (
                                        <ChevronDownIcon className="h-4 w-4 text-slate-400" />
                                    )}
                                </button>
                                {showCompleted && (
                                    <div className="mt-2 space-y-3">
                                        {completed.map((survey) => (
                                            <div
                                                key={survey.id}
                                                className="flex cursor-not-allowed items-center justify-between rounded-2xl border border-slate-300 bg-white p-4 opacity-60 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <CheckCircleIcon className="h-6 w-6 shrink-0 text-green-600 dark:text-green-400" />
                                                    <div>
                                                        <p className="font-medium text-slate-900 dark:text-slate-100">
                                                            {survey.title}
                                                        </p>
                                                        {survey.description && (
                                                            <p className="mt-0.5 line-clamp-1 text-sm text-slate-600 dark:text-slate-400">
                                                                {
                                                                    survey.description
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <span className="text-xs font-medium text-green-600 dark:text-green-400">
                                                    Completed
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Closed surveys (accordion) */}
                        {closed.length > 0 && (
                            <div>
                                <button
                                    onClick={() => setShowClosed((v) => !v)}
                                    className="flex w-full items-center justify-between rounded-lg py-2 text-left"
                                >
                                    <h2 className="text-sm font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
                                        Closed ({closed.length})
                                    </h2>
                                    {showClosed ? (
                                        <ChevronUpIcon className="h-4 w-4 text-slate-400" />
                                    ) : (
                                        <ChevronDownIcon className="h-4 w-4 text-slate-400" />
                                    )}
                                </button>
                                {showClosed && (
                                    <div className="mt-2 space-y-3">
                                        {closed.map((survey) => (
                                            <div
                                                key={survey.id}
                                                className="flex cursor-not-allowed items-center justify-between rounded-2xl border border-slate-300 bg-white p-4 opacity-60 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <LockClosedIcon className="h-6 w-6 shrink-0 text-slate-400" />
                                                    <div>
                                                        <p className="font-medium text-slate-900 dark:text-slate-100">
                                                            {survey.title}
                                                        </p>
                                                        {survey.description && (
                                                            <p className="mt-0.5 line-clamp-1 text-sm text-slate-600 dark:text-slate-400">
                                                                {
                                                                    survey.description
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                    Closed
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
